/**
 * Design phase + structure scope for client-portal releases.
 *
 * Phase: every release lands in either the **Concept** or **Engineering** phase of the
 * client approval trail. New releases carry `designPhase` from the Cloud Function; legacy
 * rows derive it from `portalTarget` (and LOD for 3D models) so the trail renders for
 * portals that pre-date the field.
 *
 * Scope: which structures (set) a release covers. Resolution order mirrors the plan:
 *   1. release snapshot `structureIds` (stamped at release time),
 *   2. current tags on the source version's track,
 *   3. forks: if the source version was later forked into per-structure tracks via
 *      `wholeProjectOrigin`, one scope per fork (the "whole project v1" case),
 *   4. tags on `sourceStreamId`,
 *   5. whole project.
 */

import type {
  PortalDesignPhase,
  PortalFileRelease,
  PortalReleaseBundle,
  PortalReleaseTarget,
  ProjectFile,
  ProjectFileStream,
} from '../types';
import { structureSelectionLabel, structureSetKey } from './structureSelectionLabel';

export const PORTAL_DESIGN_PHASES: PortalDesignPhase[] = ['concept', 'engineering'];

export const PORTAL_DESIGN_PHASE_LABELS: Record<PortalDesignPhase, string> = {
  concept: 'Concept',
  engineering: 'Engineering',
};

export const PORTAL_DESIGN_PHASE_HINTS: Record<PortalDesignPhase, string> = {
  concept: 'Early design the client approves before signing the proposal.',
  engineering: 'Engineered drawings and joinery-level models approved before fabrication.',
};

type PhaseSource = {
  designPhase?: PortalDesignPhase;
  portalTarget?: PortalReleaseTarget;
  streamKind: 'two_d_pdf' | 'three_d_html';
};

/**
 * Suggested phase in the Release modal: from the destination for PDFs; for 3D models from
 * the version's LOD (joinery / supplementary → engineering, otherwise concept). The modal
 * stamps the chosen phase explicitly, so this suggestion never has to be re-derived later.
 */
export function defaultDesignPhaseForTarget(
  portalTarget: PortalReleaseTarget,
  detailStage?: ProjectFile['detailStage'] | null,
): PortalDesignPhase {
  if (portalTarget === 'concept_elevations') return 'concept';
  if (portalTarget === 'engineered_drawings') return 'engineering';
  return detailStage === 'joinery' || detailStage === 'supplementary_details' ? 'engineering' : 'concept';
}

/**
 * Phase for a release or bundle: the explicit field first, else derived from the portal
 * target alone. Legacy 3D releases land in Concept because the portal's 3D Model step is a
 * concept-phase deliverable. Deliberately ignores hub data (LOD) so the client page and the
 * staff "Client view" can never disagree — the client cannot read hub files.
 */
export function portalReleaseDesignPhase(release: PhaseSource): PortalDesignPhase {
  if (release.designPhase === 'concept' || release.designPhase === 'engineering') return release.designPhase;
  const target: PortalReleaseTarget =
    release.portalTarget || (release.streamKind === 'three_d_html' ? 'model' : 'engineered_drawings');
  if (target === 'engineered_drawings') return 'engineering';
  return 'concept';
}

export interface ReleaseStructureScope {
  /** Stable key for the structure set (`__whole_project__` when empty). */
  key: string;
  structureIds: string[];
  structureNames: string[];
  /** "Whole project" / "Barn" / "Barn + Pavilion" … */
  label: string;
  /** True when the scope was inferred from a later fork rather than the release itself. */
  inferredFromFork?: boolean;
}

export function wholeProjectScope(): ReleaseStructureScope {
  return {
    key: structureSetKey([]),
    structureIds: [],
    structureNames: [],
    label: structureSelectionLabel([]),
  };
}

function scopeFromTags(
  ids: string[] | undefined,
  names: string[] | undefined,
  inferredFromFork?: boolean,
): ReleaseStructureScope | null {
  const cleanIds = (ids ?? []).map(s => String(s || '').trim()).filter(Boolean);
  if (cleanIds.length === 0) return null;
  const cleanNames = cleanIds.map((id, i) => {
    const n = (names ?? [])[i];
    return n && n.trim() ? n.trim() : id;
  });
  return {
    key: structureSetKey(cleanIds),
    structureIds: cleanIds,
    structureNames: cleanNames,
    label: structureSelectionLabel(cleanNames),
    ...(inferredFromFork ? { inferredFromFork: true } : {}),
  };
}

export interface ResolveScopeContext {
  files: ProjectFile[];
  streams: ProjectFileStream[];
}

/**
 * The track a released file lives on now, when staff have moved it off the track
 * it was published from. Null when it is still on that track, or when the portal
 * has no hub file to check.
 */
export function releaseMovedToStream(
  release: Pick<PortalFileRelease, 'sourceFileId' | 'sourceStreamId'>,
  ctx: ResolveScopeContext,
): ProjectFileStream | null {
  const sourceStreamId = (release.sourceStreamId || '').trim();
  if (!sourceStreamId || !release.sourceFileId) return null;
  const file = ctx.files.find(item => item.id === release.sourceFileId);
  const streamId = (file?.streamId || '').trim();
  if (!streamId || streamId === sourceStreamId) return null;
  return ctx.streams.find(stream => stream.id === streamId) ?? null;
}

/**
 * Structure scope(s) a release covers. Usually one scope; a whole-project release that
 * was later forked into per-structure tracks yields one scope per fork so the trail can
 * draw it as the shared trunk feeding every lane. A file moved onto another structure
 * track follows that track instead of the structure saved at release time.
 */
export function resolveReleaseStructureScopes(
  release: Pick<PortalFileRelease, 'sourceFileId' | 'sourceStreamId' | 'structureIds' | 'structureNames'>,
  ctx: ResolveScopeContext,
): ReleaseStructureScope[] {
  const movedTo = releaseMovedToStream(release, ctx);
  if (movedTo) {
    return [scopeFromTags(movedTo.structureIds, movedTo.structureNames) ?? wholeProjectScope()];
  }

  // 1. Snapshot stamped at release time.
  const snap = scopeFromTags(release.structureIds, release.structureNames);
  if (snap) return [snap];

  const streamById = new Map<string, ProjectFileStream>();
  for (const s of ctx.streams) if (s.id) streamById.set(s.id, s);
  const fileById = new Map<string, ProjectFile>();
  for (const f of ctx.files) if (f.id) fileById.set(f.id, f);

  // 2. Current tags on the source version's track.
  const sourceFile = release.sourceFileId ? fileById.get(release.sourceFileId) : undefined;
  const fileStream = sourceFile?.streamId ? streamById.get(sourceFile.streamId) : undefined;
  const fromFileStream = fileStream ? scopeFromTags(fileStream.structureIds, fileStream.structureNames) : null;
  if (fromFileStream) return [fromFileStream];

  // 3. The source version lives on a whole-project root that was later split:
  //    every structure track forked from it inherits the version, one scope per fork.
  //    (Legacy copies tagged `wholeProjectOrigin` resolve the same way.)
  if (release.sourceFileId) {
    const forkIds = new Set<string>();
    if (fileStream?.id) {
      for (const fid of fileStream.forkedToStreamIds ?? []) forkIds.add(fid);
      for (const s of ctx.streams) if (s.id && s.forkedFromStreamId === fileStream.id) forkIds.add(s.id);
    }
    for (const f of ctx.files) {
      if (f.streamId && f.wholeProjectOrigin?.fileId === release.sourceFileId) forkIds.add(f.streamId);
    }
    const forks: ReleaseStructureScope[] = [];
    const seen = new Set<string>();
    for (const fid of Array.from(forkIds)) {
      const forkStream = fid ? streamById.get(fid) : undefined;
      const scope = forkStream ? scopeFromTags(forkStream.structureIds, forkStream.structureNames, true) : null;
      if (scope && !seen.has(scope.key)) {
        seen.add(scope.key);
        forks.push(scope);
      }
    }
    if (forks.length > 0) return forks;
  }

  // 4. Tags on the stream id stored on the release.
  const relStream = release.sourceStreamId ? streamById.get(release.sourceStreamId) : undefined;
  const fromRelStream = relStream ? scopeFromTags(relStream.structureIds, relStream.structureNames) : null;
  if (fromRelStream) return [fromRelStream];

  // 5. Whole project.
  return [wholeProjectScope()];
}

/** Scope for a bundle: union of member releases' scopes (deduped by key). */
export function resolveBundleStructureScopes(
  bundle: Pick<PortalReleaseBundle, 'releaseIds' | 'structureIds' | 'structureNames'>,
  releases: PortalFileRelease[],
  ctx: ResolveScopeContext,
): ReleaseStructureScope[] {
  const snap = scopeFromTags(bundle.structureIds, bundle.structureNames);
  if (snap) return [snap];
  const byId = new Map<string, PortalFileRelease>();
  for (const r of releases) if (r.id) byId.set(r.id, r);
  const out: ReleaseStructureScope[] = [];
  const seen = new Set<string>();
  for (const id of bundle.releaseIds) {
    const r = byId.get(id);
    if (!r) continue;
    for (const s of resolveReleaseStructureScopes(r, ctx)) {
      if (seen.has(s.key)) continue;
      seen.add(s.key);
      out.push(s);
    }
  }
  return out.length > 0 ? out : [wholeProjectScope()];
}
